import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import constant from "config/constant";
import { wrapBaseApi } from "util/apiWrapper";
import ls from "util/secureStorage";

// console.log("AUTH HEADER", _headers);

// Define a service using a base URL and expected endpoints
export const walletApi = createApi({
  reducerPath: "wallet",
  // refetchOnReconnect: true,
  refetchOnMountOrArgChange: true,
  tagTypes: [
    "walletBalance",
    "earnWallet",
    "stakings",
    "stakingPlans",
    "checkAddress",
  ],
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_WALLET_API_ENDPOINT,
    prepareHeaders: (headers, _) => {
      const auth = ls.get(constant.AUTH_TOKEN_KEY);

      headers.set("Authorization", auth.token);
      headers.set("Content-Type", "application/json");
      headers.set("Cache-Control", "no-cache, no-store, must-revalidate");
      return headers;
    },
  }),
  endpoints: (builder) => ({
    checkReceiverIsInternal: builder.query({
      query: ({ coin, address, query }) =>
        // wrapBaseApi(`/wallet/address/check/${address}${query}`),
        wrapBaseApi(`/wallet/address/${coin}/${address}/check${query}`),
      transformResponse: (res) => res?.data,
      providesTags: ["checkAddress"],
    }),
    getStakings: builder.query({
      query: (queries) => wrapBaseApi(`/wallet/staking?${queries}`),
      transformResponse: (res) => res?.data,
      providesTags: ["stakings"],
    }),
    getStakingPlans: builder.query({
      query: () => wrapBaseApi(`/wallet/staking/plans`),
      transformResponse: (res) => res?.data,
      providesTags: ["stakingPlans"],
    }),
    getStakingById: builder.query({
      query: (id) => wrapBaseApi(`/wallet/staking/${id}`),
      transformResponse: (res) => res?.data,
      // providesTags: ["stakings"],
    }),
    getMinimumWithdrawalAmount: builder.query({
      query: (coin) => wrapBaseApi(`/wallet/withdrawal/get-minimum/${coin}`),
      transformResponse: (res) => res?.data,
      // providesTags: ["stakings"],
    }),
    getLoanById: builder.query({
      query: (id) => wrapBaseApi(`/wallet/loans/${id}`),
      transformResponse: (res) => res?.data,
    }),
    getAffiliates: builder.query({
      query: (queries) => wrapBaseApi(`/account/affiliates?${queries ?? ""}`),
      transformResponse: (res) => res?.data,
    }),
    getAffiliateTransactions: builder.query({
      query: (queries) =>
        // For user bonuses history
        // `/wallet/earning/affiliates?referredUserId=61a95b83bd68c50013151890&limit=4&page=1&afterDate=2022-03-01&beforeDate=2022-03-14`
        // For coin referral bonuses
        // `https://userapp-staging.bitmama.io/wallet/earning/affiliates?unit=celo&page=1
        wrapBaseApi(`/wallet/earning/affiliates?${queries}`),
      transformResponse: (res) => res?.data,
    }),
    getAffiliateEarnings: builder.query({
      query: (queries) =>
        wrapBaseApi(`/wallet/earning/affiliates?${queries ?? ""}`),
      transformResponse: (res) => res?.data,
    }),
    getAffiliateEarningDetails: builder.query({
      query: (id) => wrapBaseApi(`/wallet/earning/${id}`),
      transformResponse: (res) => res?.data,
    }),
    getAffiliateEarningSummary: builder.query({
      query: (id) => wrapBaseApi(`/account/affiliates/earning-summary`),
      transformResponse: (res) => res?.data,
    }),

    // getAffiliateLeaderboard: builder.query({
    //   query: (INPUT) => ({
    //     url: wrapBaseApi(`/wallet/api/v2/deposit/${input.id}/paid`),
    //     method: "POST",
    //     body: input,
    //   }),
    //   transformResponse: (res) => res,
    // }),
    getLoanBalance: builder.query({
      query: () => wrapBaseApi(`/wallet/loans/balance`),
      transformResponse: (res) => res?.data,
    }),
    getApplicableLoanRates: builder.query({
      query: () => wrapBaseApi(`/wallet/loans/applicable-rates`),
      transformResponse: (res) => res?.data,
    }),
    getLoanProfile: builder.query({
      query: () => wrapBaseApi(`/wallet/loans/profile/own`),
      transformResponse: (res) => res?.data,
    }),
    getLoanableCoins: builder.query({
      query: () => wrapBaseApi(`/wallet/loans/loan-coins`),
      transformResponse: (res) => res?.data,
    }),
    getApplicableLoanDuration: builder.query({
      query: () => wrapBaseApi(`/wallet/loans/applicable-durations`),
      transformResponse: (res) => res?.data,
    }),
    getLoanRecords: builder.query({
      query: (queries) => wrapBaseApi(`/wallet/loans?${queries ?? ""}`),
      transformResponse: (res) => res?.data,
    }),
    getWallets: builder.query({
      query: () => wrapBaseApi(`/wallets`),
      transformResponse: (res) => res?.data?.addresses?.data,
      providesTags: ["walletBalance"],
    }),
    getEarnWallet: builder.query({
      query: () => wrapBaseApi(`/wallet/staking/wallet`),
      transformResponse: (res) => res?.data,
      providesTags: ["earnWallet"],
    }),
    getDepositConfig: builder.query({
      query: (method) =>
        wrapBaseApi(`/wallet/deposit/config/${method ? method : ""}`),
      transformResponse: (res) => res?.data,
      providesTags: ["earnWallet"],
    }),
    getWalletTransations: builder.query({
      query: (wallet) =>
        wrapBaseApi(`/wallet/api/v2/transactions?unit=${wallet}&limit=10`),
      transformResponse: (res) => res?.data,
    }),
    getWalletTransationsV2: builder.query({
      query: (queries) => wrapBaseApi(`/wallet/api/v2/transactions?${queries}`),
      transformResponse: (res) => res?.data,
    }),
    getWalletWithdrawals: builder.query({
      query: (id) => wrapBaseApi(`/wallet/withdrawal/${id}`),
      transformResponse: (res) => res?.data,
    }),
    depositFunds: builder.mutation({
      query: (input) => ({
        url: wrapBaseApi(`/wallet/api/v2/deposit/${input.id}/paid`),
        method: "PUT",
        body: input,
      }),
      transformResponse: (res) => res?.data,
    }),
    cancelFundDeposit: builder.mutation({
      query: (id) => ({
        url: wrapBaseApi(`/wallet/deposit/${id}/cancel`),
        method: "DELETE",
      }),
      transformResponse: (res) => res?.data,
    }),
    fundFiatWallet: builder.mutation({
      query: (input) => ({
        url: (() => {
          const url = wrapBaseApi(`/wallet/deposit`);
          console.log("FUND URL", url);
          return url;
        })(),
        method: "POST",
        body: input,
      }),
      transformResponse: (res) => res?.data,
    }),
    processPayment: builder.mutation({
      query: ({ txId }) => ({
        url: wrapBaseApi(`/wallet/payments/flutterwave/process/${txId}`),
        method: "POST",
        body: {},
      }),
      transformResponse: (res) => res?.data,
    }),
    withdrawCrypto: builder.mutation({
      query: (input) => ({
        url: wrapBaseApi(`/wallet/withdrawal`),
        method: "POST",
        body: input,
      }),
      transformResponse: (res) => res?.data,
    }),
    requestLoan: builder.mutation({
      query: (input) => ({
        url: wrapBaseApi(`/wallet/loans/`),
        method: "POST",
        body: input,
      }),
      transformResponse: (res) => res?.data,
    }),
    paybackLoan: builder.mutation({
      query: (input) => ({
        url: wrapBaseApi(`/wallet/loans/${input.id}/payback`),
        method: "POST",
        body: input,
      }),
      transformResponse: (res) => res?.data,
    }),
    joinAffiliate: builder.mutation({
      query: (input) => ({
        url: wrapBaseApi(`/account/affiliate`),
        method: "PUT",
        body: { join: true },
      }),
      transformResponse: (res) => res?.data,
    }),
    affiliateLeaderboard: builder.mutation({
      query: ({ queries, ...input }) => ({
        // url: wrapBaseApi(`/account/affiliates/leaderboard?${queries ?? ""}`),
        url: wrapBaseApi(`/account/affiliates/leaderboard-ranking`),
        method: "POST",
        body: input,
      }),
      transformResponse: (res) => res?.data,
    }),
    fundEarnWallet: builder.mutation({
      query: (input) => ({
        // url: wrapBaseApi(`/account/affiliates/leaderboard?${queries ?? ""}`),
        url: wrapBaseApi(`/wallet/staking/fund`),
        method: "POST",
        body: input,
      }),
      transformResponse: (res) => res?.data,
    }),
    withdrawEarnWallet: builder.mutation({
      query: (input) => ({
        // url: wrapBaseApi(`/account/affiliates/leaderboard?${queries ?? ""}`),
        url: wrapBaseApi(`/wallet/staking/withdraw`),
        method: "POST",
        body: input,
      }),
      transformResponse: (res) => res?.data,
    }),
    stake: builder.mutation({
      query: (input) => ({
        // url: wrapBaseApi(`/account/affiliates/leaderboard?${queries ?? ""}`),
        url: wrapBaseApi(`/wallet/staking/new`),
        method: "POST",
        body: input,
      }),
      transformResponse: (res) => res?.data,
    }),
    calcReturn: builder.mutation({
      query: (input) => ({
        url: wrapBaseApi(`/wallet/staking/calc-returns`),
        method: "POST",
        body: input,
      }),
      transformResponse: (res) => res?.data,
    }),
    updateSubscription: builder.mutation({
      query: ({ id, ...input }) => ({
        url: wrapBaseApi(`/wallet/staking/${id}/update`),
        method: "PATCH",
        body: input,
      }),
      transformResponse: (res) => res?.data,
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints...

export const {
  useCheckReceiverIsInternalQuery,
  useGetStakingPlansQuery,
  useGetStakingsQuery,
  useGetStakingByIdQuery,
  useCalcReturnMutation,
  useGetApplicableLoanRatesQuery,
  useGetEarnWalletQuery,
  useGetAffiliatesQuery,
  useGetAffiliateEarningsQuery,
  useGetWalletWithdrawalsQuery,
  useGetAffiliateTransactionsQuery,
  useGetMinimumWithdrawalAmountQuery,
  useGetAffiliateEarningDetailsQuery,
  // useGetAffiliateLeaderboardQuery,
  useGetAffiliateEarningSummaryQuery,

  useGetLoanableCoinsQuery,
  useGetApplicableLoanDurationQuery,
  useGetLoanProfileQuery,
  useGetLoanBalanceQuery,
  useGetLoanByIdQuery,
  useGetWalletsQuery,
  useGetLoanRecordsQuery,
  useGetWalletTransationsQuery,
  useGetWalletTransationsV2Query,
  useGetDepositConfigQuery,
  useDepositFundsMutation,
  useWithdrawCryptoMutation,
  useFundFiatWalletMutation,
  useProcessPaymentMutation,
  useCancelFundDepositMutation,
  useRequestLoanMutation,
  usePaybackLoanMutation,

  useUpdateSubscriptionMutation,

  useJoinAffiliateMutation,
  useAffiliateLeaderboardMutation,
  useFundEarnWalletMutation,
  useWithdrawEarnWalletMutation,
  useStakeMutation,
} = walletApi;

export default walletApi;
